import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  ComposedChart,
  Area,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Icons } from '../components/Icons';
import { useCoinPrices } from '../hooks/queries/useCoinGecko';
import {
  calculateBitcoinRetirement,
  getRetirementSummary,
  formatCurrency,
  formatBitcoin,
} from '../services/bitcoinRetirementCalculator';
import type {
  BitcoinRetirementInput,
  BitcoinRetirementResult,
  AnnualTrackingData,
} from '../types/bitcoinRetirement.types';
import './bitcoinRetirement.css';

// Default input values
const DEFAULT_INPUT: BitcoinRetirementInput = {
  currentAge: 30,
  lifeExpectancy: 86,
  currentSavingsInBitcoin: 0.5,
  annualBuyInFiat: 0,
  bitcoinCAGR: 20,
  desiredRetirementIncome: 120000,
  inflationRate: 2,
  optimized: true,
};

// Input field configuration
interface InputFieldConfig {
  key: keyof BitcoinRetirementInput;
  labelKey: string;
  min: number;
  max: number;
  step: number;
  suffix?: string;
  prefix?: string;
}

const INPUT_FIELDS: InputFieldConfig[] = [
  { key: 'currentAge', labelKey: 'currentAge', min: 18, max: 80, step: 1, suffix: ' ans' },
  { key: 'lifeExpectancy', labelKey: 'lifeExpectancy', min: 60, max: 120, step: 1, suffix: ' ans' },
  { key: 'currentSavingsInBitcoin', labelKey: 'currentSavings', min: 0, max: 100, step: 0.01, prefix: '₿' },
  { key: 'annualBuyInFiat', labelKey: 'annualBuy', min: 0, max: 500000, step: 1000, prefix: '$' },
  { key: 'bitcoinCAGR', labelKey: 'bitcoinCAGR', min: 0, max: 100, step: 1, suffix: '%' },
  { key: 'desiredRetirementIncome', labelKey: 'retirementIncome', min: 10000, max: 1000000, step: 5000, prefix: '$' },
  { key: 'inflationRate', labelKey: 'inflationRate', min: 0, max: 20, step: 0.5, suffix: '%' },
];

export default function BitcoinRetirement() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current Bitcoin price
  const { data: coinPrices } = useCoinPrices(['bitcoin']);
  const currentBtcPrice = coinPrices?.bitcoin?.usd || 100000;

  // Parse URL params into input
  const getInputFromParams = useCallback((): BitcoinRetirementInput => {
    return {
      currentAge: Number(searchParams.get('currentAge')) || DEFAULT_INPUT.currentAge,
      lifeExpectancy: Number(searchParams.get('lifeExpectancy')) || DEFAULT_INPUT.lifeExpectancy,
      currentSavingsInBitcoin: Number(searchParams.get('currentSavings')) || DEFAULT_INPUT.currentSavingsInBitcoin,
      annualBuyInFiat: Number(searchParams.get('annualBuy')) || DEFAULT_INPUT.annualBuyInFiat,
      bitcoinCAGR: Number(searchParams.get('bitcoinCagr')) || DEFAULT_INPUT.bitcoinCAGR,
      desiredRetirementIncome: Number(searchParams.get('desiredRetirementIncome')) || DEFAULT_INPUT.desiredRetirementIncome,
      inflationRate: Number(searchParams.get('inflationRate')) || DEFAULT_INPUT.inflationRate,
      optimized: searchParams.get('optimized') !== 'false',
    };
  }, [searchParams]);

  const [input, setInput] = useState<BitcoinRetirementInput>(getInputFromParams);
  const [result, setResult] = useState<BitcoinRetirementResult | null>(null);
  const [activeChart, setActiveChart] = useState<'savings' | 'price' | 'flow'>('savings');

  // Update URL when input changes
  const updateUrlParams = useCallback((newInput: BitcoinRetirementInput) => {
    const params = new URLSearchParams();
    params.set('currentAge', String(newInput.currentAge));
    params.set('lifeExpectancy', String(newInput.lifeExpectancy));
    params.set('currentSavings', String(newInput.currentSavingsInBitcoin));
    params.set('annualBuy', String(newInput.annualBuyInFiat));
    params.set('bitcoinCagr', String(newInput.bitcoinCAGR));
    params.set('desiredRetirementIncome', String(newInput.desiredRetirementIncome));
    params.set('inflationRate', String(newInput.inflationRate));
    params.set('optimized', String(newInput.optimized));
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  // Calculate results when input changes
  useEffect(() => {
    const calculatedResult = calculateBitcoinRetirement(input, currentBtcPrice);
    setResult(calculatedResult);
  }, [input, currentBtcPrice]);

  // Handle input change
  const handleInputChange = (key: keyof BitcoinRetirementInput, value: number | boolean) => {
    const newInput = { ...input, [key]: value };
    setInput(newInput);
    updateUrlParams(newInput);
  };

  // Copy shareable URL
  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
  };

  // Get summary
  const summary = useMemo(() => {
    if (!result) return null;
    return getRetirementSummary(result, input);
  }, [result, input]);

  // Format chart data
  const chartData = useMemo(() => {
    if (!result) return [];
    return result.dataSet.map((item) => ({
      ...item,
      savingsFiatK: item.savingsFiat / 1000,
      bitcoinPriceK: item.bitcoinPrice / 1000,
      annualBudgetK: item.annualRetirementBudget / 1000,
    }));
  }, [result]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as AnnualTrackingData;
      return (
        <div className="retirement-tooltip">
          <div className="tooltip-title">
            {t('bitcoinRetirement.age')}: {data.age} ({data.year})
          </div>
          <div className="tooltip-row">
            <span className="tooltip-label">{t('bitcoinRetirement.btcHoldings')}</span>
            <span className="tooltip-value bitcoin">{formatBitcoin(data.savingsBitcoin)}</span>
          </div>
          <div className="tooltip-row">
            <span className="tooltip-label">{t('bitcoinRetirement.portfolioValue')}</span>
            <span className="tooltip-value green">{formatCurrency(data.savingsFiat)}</span>
          </div>
          <div className="tooltip-row">
            <span className="tooltip-label">{t('bitcoinRetirement.btcPrice')}</span>
            <span className="tooltip-value blue">{formatCurrency(data.bitcoinPrice)}</span>
          </div>
          {data.phase === 'retirement' && (
            <div className="tooltip-row">
              <span className="tooltip-label">{t('bitcoinRetirement.annualBudget')}</span>
              <span className="tooltip-value purple">{formatCurrency(data.annualRetirementBudget)}</span>
            </div>
          )}
          <div className="tooltip-row">
            <span className="tooltip-label">{t('bitcoinRetirement.btcFlow')}</span>
            <span className={`tooltip-value ${data.bitcoinFlow >= 0 ? 'green' : 'red'}`}>
              {data.bitcoinFlow >= 0 ? '+' : ''}{formatBitcoin(data.bitcoinFlow)}
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bitcoin-retirement-container">
      {/* Header */}
      <div className="bitcoin-retirement-header">
        <div className="header-title">
          <h1>
            <Icons.Bitcoin size={32} />
            {t('bitcoinRetirement.title')}
          </h1>
          <p className="header-subtitle">
            {t('bitcoinRetirement.subtitle')}
          </p>
          <div className="header-price-info">
            {t('bitcoinRetirement.currentBtcPrice')}:
            <span className="price">{formatCurrency(currentBtcPrice)}</span>
          </div>
        </div>
        <button className="share-button" onClick={handleShare} title={t('bitcoinRetirement.share', 'Partager')}>
          <Icons.Copy size={16} />
          {t('bitcoinRetirement.share', 'Copier le lien')}
        </button>
      </div>

      <div className="bitcoin-retirement-layout">
        {/* Parameters Panel */}
        <div className="parameters-panel">
          <div className="parameters-header">
            <Icons.Settings size={20} />
            {t('bitcoinRetirement.parameters')}
          </div>

          {INPUT_FIELDS.map((field) => (
            <div key={field.key} className="parameter-group">
              <div className="parameter-label">
                <span className="parameter-label-text">
                  {t(`bitcoinRetirement.${field.labelKey}`)}
                </span>
                <span className="parameter-value">
                  {field.prefix}{typeof input[field.key] === 'number' ? input[field.key].toLocaleString() : input[field.key]}{field.suffix}
                </span>
              </div>
              <input
                type="range"
                min={field.min}
                max={field.max}
                step={field.step}
                value={input[field.key] as number}
                onChange={(e) => handleInputChange(field.key, Number(e.target.value))}
                className="parameter-slider"
              />
              <div className="parameter-range-labels">
                <span>{field.prefix}{field.min.toLocaleString()}{field.suffix}</span>
                <span>{field.prefix}{field.max.toLocaleString()}{field.suffix}</span>
              </div>
            </div>
          ))}

          {/* Optimized toggle */}
          <div className="optimized-toggle">
            <div
              className="toggle-container"
              onClick={() => handleInputChange('optimized', !input.optimized)}
            >
              <div className="toggle-info">
                <div className="toggle-label">
                  {t('bitcoinRetirement.optimizedMode')}
                </div>
                <div className="toggle-description">
                  {t('bitcoinRetirement.optimizedModeDesc')}
                </div>
              </div>
              <div className={`toggle-switch ${input.optimized ? 'active' : ''}`}>
                <div className="toggle-knob" />
              </div>
            </div>
          </div>
        </div>

        {/* Results Panel */}
        <div className="results-panel">
          {/* Summary Cards */}
          {summary && result && (
            <div className="summary-cards">
              <div className={`summary-card ${result.canRetire ? 'can-retire' : 'cannot-retire'}`}>
                <div className="summary-card-label">{t('bitcoinRetirement.retirementAge')}</div>
                <div className={`summary-card-value ${result.canRetire ? 'green' : 'red'}`}>
                  {summary.retirementAge}
                </div>
                <div className="summary-card-subtext">
                  {result.canRetire
                    ? t('bitcoinRetirement.yearsAway', { years: summary.yearsUntilRetirement })
                    : t('bitcoinRetirement.cannotRetire')}
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-card-label">{t('bitcoinRetirement.btcAtRetirement')}</div>
                <div className="summary-card-value bitcoin">
                  {formatBitcoin(summary.totalBitcoinAtRetirement)}
                </div>
                <div className="summary-card-subtext">
                  @ {formatCurrency(summary.btcPriceAtRetirement)}
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-card-label">{t('bitcoinRetirement.portfolioValue')}</div>
                <div className="summary-card-value green">
                  {formatCurrency(summary.portfolioValueAtRetirement)}
                </div>
                <div className="summary-card-subtext">
                  ROI: {summary.roi.toFixed(0)}%
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-card-label">{t('bitcoinRetirement.monthlyIncome')}</div>
                <div className="summary-card-value purple">
                  {formatCurrency(summary.monthlyRetirementIncome)}
                </div>
                <div className="summary-card-subtext">
                  {formatCurrency(result.annualRetirementBudget)}/an
                </div>
              </div>
            </div>
          )}

          {/* Chart */}
          <div className="chart-container">
            <div className="chart-tabs">
              <button
                onClick={() => setActiveChart('savings')}
                className={`chart-tab ${activeChart === 'savings' ? 'active' : ''}`}
              >
                {t('bitcoinRetirement.chartSavings')}
              </button>
              <button
                onClick={() => setActiveChart('price')}
                className={`chart-tab ${activeChart === 'price' ? 'active' : ''}`}
              >
                {t('bitcoinRetirement.chartPrice')}
              </button>
              <button
                onClick={() => setActiveChart('flow')}
                className={`chart-tab ${activeChart === 'flow' ? 'active' : ''}`}
              >
                {t('bitcoinRetirement.chartFlow')}
              </button>
            </div>

            <div className="chart-wrapper">
              <ResponsiveContainer width="100%" height="100%">
                {activeChart === 'savings' ? (
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="age" stroke="var(--muted)" fontSize={12} />
                    <YAxis
                      yAxisId="left"
                      stroke="#f7931a"
                      fontSize={12}
                      tickFormatter={(v) => `₿${v.toFixed(1)}`}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      stroke="#10b981"
                      fontSize={12}
                      tickFormatter={(v) => `$${v}k`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {result && (
                      <ReferenceLine
                        x={result.retirementAge}
                        yAxisId="left"
                        stroke="#8b5cf6"
                        strokeDasharray="5 5"
                        label={{ value: t('bitcoinRetirement.retirement'), fill: '#8b5cf6', fontSize: 12 }}
                      />
                    )}
                    <Area
                      yAxisId="right"
                      type="monotone"
                      dataKey="savingsFiatK"
                      name={t('bitcoinRetirement.portfolioValueK')}
                      fill="#10b981"
                      fillOpacity={0.2}
                      stroke="#10b981"
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="savingsBitcoin"
                      name={t('bitcoinRetirement.btcHoldings')}
                      stroke="#f7931a"
                      strokeWidth={2}
                      dot={false}
                    />
                  </ComposedChart>
                ) : activeChart === 'price' ? (
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="age" stroke="var(--muted)" fontSize={12} />
                    <YAxis
                      stroke="#3b82f6"
                      fontSize={12}
                      tickFormatter={(v) => `$${v}k`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {result && (
                      <ReferenceLine
                        x={result.retirementAge}
                        stroke="#8b5cf6"
                        strokeDasharray="5 5"
                      />
                    )}
                    <Area
                      type="monotone"
                      dataKey="bitcoinPriceK"
                      name={t('bitcoinRetirement.btcPriceK')}
                      fill="#3b82f6"
                      fillOpacity={0.2}
                      stroke="#3b82f6"
                    />
                    <Line
                      type="monotone"
                      dataKey="annualBudgetK"
                      name={t('bitcoinRetirement.annualBudgetK')}
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      dot={false}
                    />
                  </ComposedChart>
                ) : (
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="age" stroke="var(--muted)" fontSize={12} />
                    <YAxis
                      stroke="#f7931a"
                      fontSize={12}
                      tickFormatter={(v) => `₿${v.toFixed(2)}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {result && (
                      <ReferenceLine
                        x={result.retirementAge}
                        stroke="#8b5cf6"
                        strokeDasharray="5 5"
                      />
                    )}
                    <ReferenceLine y={0} stroke="var(--muted)" />
                    <Bar
                      dataKey="bitcoinFlow"
                      name={t('bitcoinRetirement.btcFlow')}
                      fill="#10b981"
                    />
                  </ComposedChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>

          {/* Data Table */}
          {result && (
            <div className="data-table-container">
              <h2 className="data-table-header">{t('bitcoinRetirement.yearlyBreakdown')}</h2>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('bitcoinRetirement.age')}</th>
                    <th className="text-right">{t('bitcoinRetirement.year')}</th>
                    <th className="text-right">{t('bitcoinRetirement.btcPrice')}</th>
                    <th className="text-right">{t('bitcoinRetirement.btcHoldings')}</th>
                    <th className="text-right">{t('bitcoinRetirement.portfolioValue')}</th>
                    <th className="text-right">{t('bitcoinRetirement.btcFlow')}</th>
                    <th className="text-center">{t('bitcoinRetirement.phase')}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.dataSet.map((row) => (
                    <tr
                      key={row.key}
                      className={row.age === result.retirementAge ? 'retirement-row' : ''}
                    >
                      <td style={{ fontWeight: 600 }}>{row.age}</td>
                      <td className="text-right" style={{ color: 'var(--muted)' }}>{row.year}</td>
                      <td className="text-right value-price">
                        {formatCurrency(row.bitcoinPrice)}
                      </td>
                      <td className="text-right value-bitcoin">
                        {formatBitcoin(row.savingsBitcoin)}
                      </td>
                      <td className="text-right value-portfolio">
                        {formatCurrency(row.savingsFiat)}
                      </td>
                      <td className={`text-right ${row.bitcoinFlow >= 0 ? 'value-positive' : 'value-negative'}`}>
                        {row.bitcoinFlow >= 0 ? '+' : ''}{formatBitcoin(row.bitcoinFlow)}
                      </td>
                      <td className="text-center">
                        <span className={`phase-badge ${row.phase}`}>
                          {t(`bitcoinRetirement.${row.phase}`)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Disclaimer */}
          <div className="disclaimer">
            <strong>{t('bitcoinRetirement.disclaimer')}:</strong>
            <ul>
              <li>{t('bitcoinRetirement.disclaimerProjections')}</li>
              <li>{t('bitcoinRetirement.disclaimerVolatility')}</li>
              <li>{t('bitcoinRetirement.disclaimerAdvice')}</li>
            </ul>
            <div className="disclaimer-credits">
              {t('bitcoinRetirement.credits')}{' '}
              <a
                href="https://github.com/pampeanodev/btcretirementcalc"
                target="_blank"
                rel="noopener noreferrer"
              >
                @pampeanodev
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
